import React, { useState } from "react";

export type ImageStyle = "stock" | "flat_art" | "none" | "mixed";

interface ImageStyleStepProps {
  onSelect: (style: ImageStyle) => void;
  isLoading: boolean;
}

/* Preview thumbnails for each image style */
const StockPreview: React.FC = () => (
  <img
    src="/images/stock-example.jpg"
    alt="Stock photography example"
    style={{ width: "120px", height: "68px", objectFit: "cover", borderRadius: "6px", display: "block" }}
  />
);

const FlatArtPreview: React.FC = () => (
  <svg width="120" height="68" viewBox="0 0 120 68" style={{ borderRadius: "6px", display: "block" }}>
    <rect width="120" height="68" fill="#F0F4FF" rx="6" />
    {/* Abstract shapes */}
    <circle cx="30" cy="28" r="16" fill="#6C5CE7" opacity="0.85" />
    <rect x="55" y="12" width="24" height="24" rx="4" fill="#00B894" opacity="0.85" />
    <polygon points="100,12 115,40 85,40" fill="#FDCB6E" opacity="0.85" />
    {/* Decorative dots */}
    <circle cx="22" cy="55" r="4" fill="#FF7675" opacity="0.7" />
    <circle cx="50" cy="52" r="5" fill="#74B9FF" opacity="0.7" />
    <circle cx="78" cy="56" r="3" fill="#A29BFE" opacity="0.7" />
    <circle cx="102" cy="54" r="4" fill="#55EFC4" opacity="0.7" />
    <rect width="120" height="68" fill="none" stroke="#ddd" strokeWidth="1" rx="6" />
  </svg>
);

const MixedPreview: React.FC = () => (
  <div style={{ display: "flex", gap: "4px" }}>
    <img
      src="/images/stock-example.jpg"
      alt="Stock photo side"
      style={{ width: "58px", height: "68px", objectFit: "cover", borderRadius: "6px 0 0 6px", display: "block" }}
    />
    <svg width="58" height="68" viewBox="0 0 58 68" style={{ borderRadius: "0 6px 6px 0", display: "block" }}>
      <rect width="58" height="68" fill="#F0F4FF" />
      <circle cx="18" cy="22" r="10" fill="#6C5CE7" opacity="0.85" />
      <rect x="33" y="14" width="14" height="14" rx="3" fill="#00B894" opacity="0.85" />
      <circle cx="16" cy="50" r="6" fill="#FDCB6E" opacity="0.7" />
      <polygon points="42,40 52,56 32,56" fill="#FF7675" opacity="0.7" />
    </svg>
  </div>
);

const OPTIONS: { value: ImageStyle; label: string; description: string; preview?: React.FC }[] = [
  {
    value: "stock",
    label: "Stock Photography",
    description: "Professional, photorealistic images that give your newsletter a familiar, polished feel.",
    preview: StockPreview,
  },
  {
    value: "flat_art",
    label: "Flat Illustrations",
    description: "Clean, stylized vector art for a smoother, more modern reading experience.",
    preview: FlatArtPreview,
  },
  {
    value: "mixed",
    label: "Mixed (Our Pick)",
    description: "We'll choose the best style for each image based on the content around it.",
    preview: MixedPreview,
  },
  {
    value: "none",
    label: "No Images",
    description: "Great for purely informational newsletters where the writing is the focus.",
  },
];

const ImageStyleStep: React.FC<ImageStyleStepProps> = ({ onSelect, isLoading }) => {
  const [selected, setSelected] = useState<ImageStyle>("mixed");

  return (
    <div style={{ maxWidth: "480px", width: "100%", textAlign: "left" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem", textAlign: "center" }}>
        Image Style
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem", textAlign: "center", fontSize: "0.9rem" }}>
        What kind of images should we generate for your newsletter?
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "1.5rem" }}>
        {OPTIONS.map((opt) => {
          const isSelected = selected === opt.value;
          const Preview = opt.preview;
          return (
            <div
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              style={{
                background: "#fff",
                borderRadius: "10px",
                padding: "14px 18px",
                border: isSelected ? "2px solid #000" : "1px solid #eee",
                cursor: "pointer",
                transition: "border 0.15s ease",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <div style={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                border: isSelected ? "6px solid #000" : "2px solid #ccc",
                flexShrink: 0,
                marginTop: "2px",
                boxSizing: "border-box",
              }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "2px" }}>
                  {opt.label}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#666", lineHeight: 1.4 }}>
                  {opt.description}
                </div>
                {Preview && (
                  <div style={{ marginTop: "8px" }}>
                    <Preview />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={() => onSelect(selected)}
        disabled={isLoading}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap", maxWidth: "none",
          opacity: isLoading ? 0.5 : 1,
        }}
      >
        Continue →
      </button>
    </div>
  );
};

export default ImageStyleStep;
